import { ServicePlanModel } from '@user/store/map/typings'

export type ServiceStatusKind = 'active' | 'canceled' | 'completed' | 'unknown'

type ServiceDateField =
  | 'startDate'
  | 'nextServiceDate'
  | 'renewalDate'
  | 'cancelDate'
  | 'lastServiceDate'

export interface ServiceStatusInfo {
  kind: ServiceStatusKind
  /** Chip text — empty when there is nothing meaningful to show */
  label: string
  /** Label above the date, always describing the field the date came from */
  dateLabel: string
  dateValue: string
}

const DEFAULT_LABEL: Record<ServiceStatusKind, string> = {
  active: 'Active',
  canceled: 'Canceled',
  completed: 'Completed',
  unknown: '',
}

const DATE_LABEL: Record<ServiceDateField, string> = {
  startDate: 'Start date',
  nextServiceDate: 'Next service',
  renewalDate: 'Renewal date',
  cancelDate: 'Canceled date',
  lastServiceDate: 'Date of service',
}

// Per status, the dates worth showing in preference order. The first field that
// holds a usable date wins and the label follows that same field, so the caption
// can never disagree with the date rendered under it.
const DATE_FIELDS: Record<ServiceStatusKind, ServiceDateField[]> = {
  canceled: ['cancelDate', 'lastServiceDate'],
  active: ['startDate', 'nextServiceDate', 'renewalDate'],
  completed: ['lastServiceDate', 'startDate'],
  unknown: ['lastServiceDate', 'startDate'],
}

export function formatServiceDate(date: string | Date | null | undefined) {
  const parsed = parseDate(date)
  if (!parsed) return '-'

  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  })
}

export function getRecurrenceLabel(service: ServicePlanModel) {
  const recurrence = service.recurrence?.trim().toLowerCase()
  return recurrence === 'one-time' ? 'One-Time' : 'Recurring'
}

export function getServiceStatusInfo(
  service: ServicePlanModel,
): ServiceStatusInfo {
  const kind = getStatusKind(service)
  const fields = DATE_FIELDS[kind]
  // Nothing usable to show falls back to the first field so the caption still
  // reads as the status implies, next to a "-".
  const field = fields.find((name) => parseDate(service[name])) ?? fields[0]

  // One-time ServiceOrders/Invoices represent a single occurrence, not a "start".
  // For those, we intentionally render the (single-occurrence) date using the
  // "Date of service" label (cancellation still wins and uses "Canceled date").
  const isOneTime = getRecurrenceLabel(service) === 'One-Time'
  const dateLabel =
    kind !== 'canceled' && isOneTime
      ? DATE_LABEL.lastServiceDate
      : DATE_LABEL[field]

  return {
    kind,
    label: service.status?.trim() || DEFAULT_LABEL[kind],
    dateLabel,
    dateValue: formatServiceDate(service[field]),
  }
}

function getStatusKind(service: ServicePlanModel): ServiceStatusKind {
  const fromApi = normalizeStatus(service.status)
  if (fromApi) return fromApi

  // Cancellation is terminal and wins regardless of the Active flag.
  if (service.cancelDate) return 'canceled'

  // Active is authoritative when present: a plan can be Active even though it
  // also carries a (now stale) lastServiceDate from a prior service (Bug #2).
  if (service.active) return 'active'

  // Without an explicit Active flag the dates are the only signal: an
  // upcoming service (or renewal) means the plan is still running, otherwise
  // the work it covers is already done.
  if (hasUpcomingService(service)) return 'active'
  if (service.lastServiceDate) return 'completed'
  if (service.recurrence && service.startDate) return 'active'

  return 'unknown'
}

function normalizeStatus(status: string | null | undefined) {
  const value = status?.trim().toLowerCase()
  if (!value) return null
  if (value.startsWith('cancel')) return 'canceled' as const
  if (value.startsWith('complete')) return 'completed' as const
  if (value.startsWith('active')) return 'active' as const
  // Any other status the API sends is rendered as-is with the default styling.
  return 'active' as const
}

function hasUpcomingService(service: ServicePlanModel) {
  const now = Date.now()
  return [service.nextServiceDate, service.renewalDate].some((date) => {
    const parsed = parseDate(date)
    return !!parsed && parsed.getTime() >= now
  })
}

function parseDate(date: string | Date | null | undefined) {
  if (!date) return null

  const parsed = new Date(date)
  return isNaN(parsed.getTime()) ? null : parsed
}
